import React, { useState, useEffect, useCallback } from 'react';
import { Table, Tag } from 'antd';
import moment from 'moment';
import { getCustomerActivityLogs } from '../../services/activityLogService';
import ActivityLogFilters from './ActivityLogFilters';
import ActivityDetailModal from './ActivityDetailModal';

const DEFAULT_FILTERS = {
  search: '',
  module: null,
  actionType: null,
  startDate: null,
  endDate: null,
};

const CustomerActivityTab = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [selectedLog, setSelectedLog] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  const fetchLogs = useCallback(async (page = 1, size = 10, f = appliedFilters) => {
    setLoading(true);
    try {
      const res = await getCustomerActivityLogs({ page: page - 1, size, ...f });
      if (res?.status) {
        setLogs(res.data.content || []);
        setPagination({ current: page, pageSize: size, total: res.data.totalElements });
      }
    } catch (err) {
      console.error('Failed to fetch customer logs:', err);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleTableChange = (pag) => fetchLogs(pag.current, pag.pageSize);

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
    fetchLogs(1, pagination.pageSize, filters);
  };

  const handleResetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    fetchLogs(1, pagination.pageSize, DEFAULT_FILTERS);
  };

  const handleRowClick = (record) => {
    setSelectedLog(record);
    setModalVisible(true);
  };

  const channelColors = {
    Web: 'green',
    Mobile: 'purple',
    USSD: 'gold',
    API: 'cyan',
  };

  const columns = [
    {
      title: 'Customer Name',
      dataIndex: 'userName',
      key: 'userName',
      render: (name) => <span style={{ fontWeight: 600, color: '#1a1a1a' }}>{name}</span>,
    },
    {
      title: 'Account Number',
      dataIndex: 'accountNumber',
      key: 'accountNumber',
      render: (acct) => (
        <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#333' }}>
          {acct || '—'}
        </span>
      ),
    },
    {
      title: 'Action Performed',
      dataIndex: 'action',
      key: 'action',
      ellipsis: true,
      render: (action) => <span style={{ color: '#333' }}>{action}</span>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (desc) => <span style={{ color: '#595959' }}>{desc || '—'}</span>,
    },
    {
      title: 'Channel',
      dataIndex: 'channel',
      key: 'channel',
      render: (channel) => (
        <Tag
          color={channelColors[channel] || 'default'}
          style={{ fontWeight: 600, borderRadius: 6, fontSize: 11 }}
        >
          {channel || '—'}
        </Tag>
      ),
    },
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 190,
      render: (ts) => (
        <span style={{ color: '#8c8c8c', fontSize: 12, fontWeight: 500 }}>
          {moment(ts).format('MMM DD, YYYY  •  hh:mm A')}
        </span>
      ),
    },
  ];

  return (
    <div>
      <ActivityLogFilters
        filters={filters}
        onFilterChange={setFilters}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        hideUserType
        hideLoginStatus
      />
      <div className="activity-table-card">
        <Table
          columns={columns}
          dataSource={logs}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `${range[0]}–${range[1]} of ${total} records`,
          }}
          onChange={handleTableChange}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
          })}
          rowClassName={(_, idx) => (idx % 2 === 0 ? 'activity-row-even' : 'activity-row-odd')}
          className="activity-table"
        />
      </div>
      <ActivityDetailModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        activity={selectedLog}
      />
    </div>
  );
};

export default CustomerActivityTab;
